import { useEditorStore } from '@/store/use-editor-store';
import { useDataSource } from './use-data-source';
import { onBeforeUnmount, onDeactivated, Ref } from 'vue';
import { buildAst } from '@/utils/ast';
import { emitter, EventType } from '@/utils/emitter.utils';
import { useLayerStore } from '@/store/use-layer-store';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { getAstColorTable } from '@/utils/ast/rootAst';
import KonvaService from '@/services/konva';
import { debounce } from 'lodash-es';
import { RendererRuntime } from '@/watch-renderer/runtime';
import { useEmitterListener } from './use-event-listener';
import { WatchfaceRenderer } from '@/watch-renderer';
import { useRoute } from 'vue-router';
import { getWatchfaceDeviceRadius } from '.';
import { getImageAndVideoDoms, getThemeRive } from './use-canvas-utils';

export type RenderReason =
  | 'canvas-scale'
  | 'shape-transformed'
  | 'theme-changed'
  | 'config-data-changed'
  | 'images-changed'
  | 'keep-data'
  | 'window-resize'
  | 'others';

export let mainRenderer: WatchfaceRenderer | null = null;

export const getRenderTheme = (data: {
  theme: StoreThemeItem;
  colorItem?: StoreThemeColorItem;
  colorMode: 'recolor' | 'colorGroup';
  themeIndex?: number;
}) => {
  const { theme, colorItem, colorMode } = data;
  const colorTable = getAstColorTable(theme);
  const themeColor = colorItem?.color ?? '';
  const themeColorIndex = theme.colorGroupTable?.findIndex(
    (item) => item.id == colorItem?.id
  );

  const item: WatchRendererOptions['renderTheme'] = {
    name: theme.name,
    type: theme.type,
    colorMode,
    themeColor:
      colorMode == 'colorGroup'
        ? colorTable.length > 1
          ? themeColor
          : undefined
        : themeColorIndex > 0
        ? themeColor
        : undefined,
    recolorMode: 'only-image',
    index: data.themeIndex ?? undefined
  };

  return item;
};

export const useCanvasRender = (
  canvasRef?: Ref<HTMLCanvasElement | undefined>
) => {
  const layerStore = useLayerStore();
  const editorStore = useEditorStore();
  const runtimeStore = useRuntimeStore();
  const watchfaceStore = useWatchfaceStore();
  const { configData } = useDataSource();

  // ast
  const _getWatchfaceAst = (theme: StoreThemeItem) => {
    const ast: Ast = buildAst({
      buildFor: 'editor',
      themeName: theme.name,
      themeType: theme.type
    }) ?? { tag: 'Watchface', attrsMap: {}, children: [] };
    return ast;
  };

  // 图片doms，当doms改变后重新render
  const _generateImgVideoDoms = async (watchfaceAst: Ast) => {
    return await getImageAndVideoDoms({
      themeId: editorStore.theme?.id ?? '',
      watchfaceAst,
      colorId: editorStore.themeColor?.id ?? ''
    });
  };
  useEmitterListener(EventType['watchface:opened-resources'], () => {
    // 图片首次加载后，还没有图片缓存
    render({ reason: 'images-changed' });
  });

  const _getRenderConfig = async () => {
    // 要渲染的主题
    const theme = editorStore.theme ??
      layerStore.themes.filter((item) => item.type == 'normal')?.[0] ?? {
        children: [],
        colorGroupTable: []
      };

    const ast = _getWatchfaceAst(theme);

    const config: WatchRendererOptions = {
      ast: ast,
      images: {},
      renderTheme: getRenderTheme({
        theme,
        colorItem: editorStore.themeColor,
        colorMode: watchfaceStore.colorMode
      }),
      scale: editorStore.watchfaceCanvas.scale,
      configData: configData.value,
      width: watchfaceStore.size.width,
      height: watchfaceStore.size.height,
      arcAuxiliary:
        editorStore.arcAuxiliary as WatchRendererOptions['arcAuxiliary'],
      clipRound: getWatchfaceDeviceRadius(watchfaceStore.deviceType ?? ''),
      // rive
      rive: await getThemeRive({
        theme,
        wathchOS: watchfaceStore.description.watchOS,
        renderer: mainRenderer
      })
    };

    return config;
  };

  const _resumeKonva = debounce((service?: KonvaService) => {
    if (service) service.enable = true;
    emitter.emit(EventType['maml:rendered']);
  }, 500);

  const route = useRoute();
  const _renderOnce = (_renderer: WatchfaceRenderer, reason: RenderReason) => {
    if (route.name != 'editor') return;
    console.time('WATCHFACE CANVAS RENDER ' + reason);
    _renderer.renderring();
    console.timeEnd('WATCHFACE CANVAS RENDER ' + reason);

    runtimeStore.reset({
      stateMap: _renderer.runtime.state,
      configData: { ..._renderer.runtime.configData },
      configDataWhole: { ..._renderer.runtime.configDataWhole },
      watchfaceAst: _renderer.options.ast
    });

    emitter.emit(EventType['maml:rendered'], {
      watchfaceAst: _renderer.options.ast,
      reason
    });
  };

  const _renderOnce5 = debounce(_renderOnce, 5);

  /**
   * 重置ast和images等所有配置信息，然后重新绘制
   * @param _renderer
   * @param reason
   */
  const _resetConfigThenRender = async (
    _renderer: WatchfaceRenderer,
    reason: RenderReason
  ) => {
    const config = await _getRenderConfig();
    return _generateImgVideoDoms(config.ast).then((res) => {
      config.images = res.images;
      config.videos = res.videos;
      _renderer.reset(config);
      _renderOnce(_renderer, reason);
    });
  };

  /**
   * 渲染元素
   * @param canvasEl [可选] 使用的canvas元素，如未提供则使用上一级的canvasRef
   * @returns
   */
  const render = (options?: {
    reason?: RenderReason;
    layouts?: Parameters<RendererRuntime['updateLayoutAttrs']>[0];
  }) => {
    const { reason, layouts } = options ?? {};

    const _canvasEl = canvasRef?.value;
    if (!_canvasEl) {
      return;
    }
    mainRenderer =
      mainRenderer ??
      new WatchfaceRenderer(
        _canvasEl,
        // 初始时，使用空的配置
        {
          ast: { tag: 'Watchface', attrsMap: {}, children: [] },
          images: {},
          videos: {},
          renderTheme: {
            name: ''
          }
        }
      );

    // 先开启konva
    const service = KonvaService.getIns();
    if (service) service.enable = true;
    mainRenderer.runtime.clearAniData();

    if (reason == 'canvas-scale') {
      const scale = editorStore.watchfaceCanvas.scale ?? 1;
      mainRenderer.setScale(scale);
      _renderOnce(mainRenderer, reason);
    } else if (reason == 'config-data-changed') {
      if (service) service.enable = false;
      // 判断是否单位类型改变
      const healthBloodPressureUnit1 =
        mainRenderer.runtime.configDataWhole['healthBloodPressureUnit'];
      const weatherTemperatureUnit1 =
        mainRenderer.runtime.configDataWhole['weatherTemperatureUnit'];
      const healthBloodPressureUnit2 =
        configData.value['healthBloodPressureUnit'];
      const weatherTemperatureUnit2 =
        configData.value['weatherTemperatureUnit'];
      if (
        healthBloodPressureUnit1 != healthBloodPressureUnit2 ||
        weatherTemperatureUnit1 != weatherTemperatureUnit2
      ) {
        _resetConfigThenRender(mainRenderer, reason).then(() => {
          _resumeKonva(service);
        });
      } else {
        mainRenderer.runtime.initConfigData(configData.value);
        _renderOnce5(mainRenderer, reason);
        _resumeKonva(service);
      }
    } else if (reason == 'shape-transformed') {
      if (layouts?.length) {
        mainRenderer.runtime.updateLayoutAttrs(layouts);
        _renderOnce(mainRenderer, reason);
      } else {
        _resetConfigThenRender(mainRenderer, reason);
      }
    } else if (reason == 'images-changed') {
      _resetConfigThenRender(mainRenderer, reason);
      mainRenderer.runtime.initConfigData(configData.value);
    } else if (reason == 'keep-data' || reason == 'window-resize') {
      _renderOnce(mainRenderer, reason);
    } else {
      _resetConfigThenRender(mainRenderer, 'others');
    }
  };

  onBeforeUnmount(() => {
    mainRenderer?.runtime?.clear();
    mainRenderer?.runtime.rive?.rive.cleanup();
  });
  onDeactivated(() => {
    mainRenderer?.runtime?.clear();
  });

  return {
    render
  };
};
